import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormPageShell } from '../../components/DataTablePanel';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import { BackButton } from '../../components/BackButton';
import {
  BanIcon,
  Button,
  Callout,
  Card,
  LoadingSpinner,
  PencilIcon,
  useConfirm,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { PaymentDetail, PaymentLine } from '../../types/payment';
import { formatDate, formatMoney } from '../../utils/format';

const lineSortGetters: Record<
  string,
  (line: PaymentLine) => string | number | boolean | null | undefined
> = {
  type: (l) => l.type,
  amount: (l) => l.amount,
  description: (l) => l.description,
};

export function PaymentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const confirm = useConfirm();
  const labels = t().payments;
  const [payment, setPayment] = useState<PaymentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('type');
  const displayLines = useMemo(
    () =>
      payment ? sortItems(payment.lines, sort, lineSortGetters) : [],
    [payment, sort],
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await api.getPayment(Number(id));
        if (!cancelled) setPayment(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleDelete() {
    if (!payment) return;
    const ok = await confirm({
      title: labels.softDelete,
      message: labels.softDeleteConfirm,
      confirmText: labels.softDelete,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await api.deletePayment(payment.id);
      navigate('/payments');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
    }
  }

  if (loading) {
    return <LoadingSpinner message={t().common.loading} />;
  }

  if (!payment) {
    return (
      <Callout variant="danger">{error || 'Η πληρωμή δεν βρέθηκε'}</Callout>
    );
  }

  return (
    <FormPageShell wide contentClassName="space-y-5">
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">
              {formatDate(payment.paymentDate)} · {payment.customer.name}
            </h2>
            <div className="mt-1 text-sm text-ink-muted">
              {payment.isMultiAllocated ? (
                labels.multiAllocated
              ) : payment.allocations.length === 1 ? (
                <>
                  {labels.invoice}:{' '}
                  <Link
                    to={`/invoices/${payment.allocations[0].invoiceId}`}
                    className="text-brand hover:underline"
                  >
                    {payment.allocations[0].invoice.invoiceNo}
                  </Link>
                </>
              ) : (
                labels.unallocated
              )}
            </div>
            <div className="mt-3 text-xl font-semibold tabular-nums">
              {formatMoney(payment.totals.total)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <BackButton fallback="/payments" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/payments/${payment.id}/edit`)}
            >
              <PencilIcon className="h-3.5 w-3.5" />
              {labels.editAction}
            </Button>
            {isAdmin ? (
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => void handleDelete()}
              >
                <BanIcon className="h-3.5 w-3.5" />
                {labels.softDelete}
              </Button>
            ) : null}
          </div>
        </div>
      </Card>

      {error ? <Callout variant="danger">{error}</Callout> : null}

      <Card className="space-y-2 p-4 text-sm">
        <Row label={labels.customer} value={payment.customer.name} />
        <Row
          label={labels.invoice}
          value={
            payment.isMultiAllocated
              ? labels.multiAllocated
              : payment.allocations[0]?.invoice.invoiceNo ??
                labels.unallocated
          }
        />
        <Row label={labels.description} value={payment.description} />
        <Row label={labels.notes} value={payment.notes} />
        <Row
          label={labels.invoicePayments}
          value={formatMoney(payment.totals.invoice)}
        />
        <Row
          label={labels.cashPayments}
          value={formatMoney(payment.totals.cash)}
        />
        <Row
          label={labels.allocated}
          value={formatMoney(payment.allocated.total)}
        />
        <Row
          label={labels.unallocatedRemainder}
          value={formatMoney(payment.unallocated.total)}
        />
      </Card>

      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">{labels.linesSection}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-left text-ink-muted">
              <tr>
                <SortableTh sortKey="type" sort={sort} onSort={toggleSort}>
                  {t().financial.type}
                </SortableTh>
                <SortableTh
                  sortKey="amount"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.amount}
                </SortableTh>
                <SortableTh
                  sortKey="description"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t().financial.description}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayLines.map((line) => (
                <tr key={line.id} className="border-t border-line">
                  <td className="px-2 py-1">
                    {line.type === 'INVOICE'
                      ? t().financial.invoice
                      : t().financial.cash}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {formatMoney(line.amount)}
                  </td>
                  <td className="px-2 py-1">{line.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {payment.allocations.length > 0 ? (
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold">
            {labels.allocationsSection}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-muted text-left text-ink-muted">
                <tr>
                  <th className="px-2 py-1">{labels.invoice}</th>
                  <th className="px-2 py-1 text-right">{t().financial.invoice}</th>
                  <th className="px-2 py-1 text-right">{t().financial.cash}</th>
                  <th className="px-2 py-1 text-right">
                    {t().financial.grandTotal}
                  </th>
                </tr>
              </thead>
              <tbody>
                {payment.allocations.map((row) => (
                  <tr
                    key={row.id ?? row.invoiceId}
                    className="border-t border-line"
                  >
                    <td className="px-2 py-1">
                      <Link
                        to={`/invoices/${row.invoiceId}`}
                        className="text-brand hover:underline"
                      >
                        {row.invoice.invoiceNo}
                      </Link>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {formatMoney(row.invoiceAmount)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {formatMoney(row.cashAmount)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums font-medium">
                      {formatMoney(row.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </FormPageShell>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      <span className="text-right">{value || '—'}</span>
    </div>
  );
}
